/** Decision tables in action (pack-decisions): run every table over the facts already in the kernel. */
import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Row, Space, Spin, Statistic, Tabs, Tag, Typography, message } from "antd";
import { ExperimentOutlined, ReloadOutlined } from "@ant-design/icons";
import { OrgScopeBar, TONE_FRAME_CLASS, TONE_ICON_CLASS, useOrgScopeStore } from "@evo-flywheel/ui";
import { kernel, type Evaluation, type TableInfo } from "../api";
import { FindingsTable, TraceNote } from "../findings";
import { labelsFor } from "../labels";
import { scopeFindings, useScopeKey } from "../modules/obligations";
import type { Finding } from "../api";

export const Rules: React.FC = () => {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [ev, setEv] = useState<Evaluation>();
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // ADR-037 (C55): findings of the chosen companies; the evaluation itself is group-wide
  const [shown, setShown] = useState<Finding[]>();
  const scopeKey = useScopeKey();
  const scoped = useOrgScopeStore((s) => s.selected.length > 0);
  useEffect(() => { if (ev) scopeFindings(ev.findings).then(setShown).catch(() => setShown(ev.findings)); }, [ev, scopeKey]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await kernel<Evaluation>("/decisions/evaluate-stored", {});
      setEv(r);
      setLabels(await labelsFor(r.findings.map((f) => f.subject)));
    } catch (e) { message.error((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => { kernel<TableInfo[]>("/decisions/tables").then(setTables).catch((e) => message.error(e.message)); run(); }, []);

  const findings = shown ?? [];
  const unmet = findings.filter((f) => f.status === "unmet");
  const blocking = unmet.filter((f) => f.severity === "blocking");
  return <div>
    <Space style={{ width: "100%", justifyContent: "space-between" }}>
      <Typography.Title level={3} style={{ margin: 0 }}><span className={TONE_ICON_CLASS}><ExperimentOutlined /></span> Regole e verifiche</Typography.Title>
      <Button icon={<ReloadOutlined />} onClick={run} loading={busy}>Rivaluta</Button>
    </Space>
    <TraceNote />
    <OrgScopeBar />
    {scoped && <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Obblighi delle società scelte (e degli elementi di gruppo, se inclusi). Le regole valutano comunque i fatti di tutto il gruppo: un obbligo può dipendere da un collegamento tra società." />}
    <Spin spinning={busy}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card className={TONE_FRAME_CLASS}><Statistic title="Fatti valutati" value={ev?.graph.nodes ?? 0} suffix={<Typography.Text type="secondary"> / {ev?.graph.edges ?? 0} collegamenti</Typography.Text>} /></Card></Col>
        <Col span={6}><Card className={TONE_FRAME_CLASS}><Statistic title="Obblighi verificati" value={findings.length} /></Card></Col>
        <Col span={6}><Card className={TONE_FRAME_CLASS}><Statistic title="Bloccanti aperti" value={blocking.length} valueStyle={{ color: blocking.length ? "#cf1322" : "#389e0d" }} /></Card></Col>
        <Col span={6}><Card className={TONE_FRAME_CLASS}><Statistic title="Avvisi e consigli" value={unmet.length - blocking.length} valueStyle={{ color: "#d48806" }} /></Card></Col>
      </Row>
      {ev && findings.length === 0 && <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="Nessuna regola ha trovato soggetti a cui applicarsi"
        description="Le tabelle scattano su fatti come 'il fornitore accede a dati personali' o 'l'asset contiene dati'. Completa un percorso guidato per creare questi collegamenti." />}
      <Tabs items={tables.map((t) => {
        const fs = findings.filter((f) => f.table === t.table);
        const open = fs.filter((f) => f.status === "unmet").length;
        return {
          key: t.table,
          label: <Space size={4}>{t.title ?? t.table}{open > 0 ? <Tag color="red">{open}</Tag> : fs.length > 0 ? <Tag color="green">ok</Tag> : null}{t.draft && <Tag>bozza</Tag>}</Space>,
          children: <Card className={TONE_FRAME_CLASS} size="small" title={<Space><Tag>{t.subject}</Tag><Typography.Text type="secondary">fase: {t.stage}</Typography.Text></Space>}>
            {fs.length ? <FindingsTable findings={fs} labels={labels} /> : <Typography.Text type="secondary">Nessun soggetto a cui applicare questa tabella.</Typography.Text>}
          </Card>,
        };
      })} />
    </Spin>
  </div>;
};
